import React, { useEffect, useState, useRef, useCallback } from "react";
import { useStore } from "../../context";
import "./index.css";
import { urlDashBoard } from "../../utils";
import { httpGet } from "../../servicios";
import { navigate } from "@reach/router";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import Swal from "sweetalert2";

function ccyFormat(num) {
  return `${num.toFixed(2)}`;
}

export default function Rubros(props) {
  const { state, dispatch } = useStore();
  const [loading, setLoading] = useState(true);
  const [rowsAll, setRowsAll] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const tableRef = useRef(null);
  const tabulatorInstance = useRef(null);

  // Helper: extract number from rubro name (e.g., "1" from "PENSION 1/10")
  const extractRubroNumber = (abrRub) => {
    const match = abrRub.match(/(\d+)\/\d+/);
    return match ? parseInt(match[1], 10) : 999;
  };

  const addCart = useCallback(
    (itemCart, sortedData) => {
      // Prevent duplicate: check if already in cart
      const key = (i) => i.periodo + i.codNiv + i.codEst + i.codRub;
      const alreadyInCart = state.cart.some((c) => key(c) === key(itemCart));
      if (alreadyInCart) {
        Swal.fire({
          icon: "warning",
          title: "Duplicado",
          text: `${itemCart.abrRub} ya está en el carrito.`,
          confirmButtonColor: "#0d1b3e",
        });
        return;
      }

      // Sequential order: all previous rows (regardless of type) must be in cart
      const pendingRows = sortedData.filter((r) => parseFloat(r.valor) > 0);
      const itemIndex = pendingRows.findIndex((r) => key(r) === key(itemCart));
      const cartKeys = new Set(state.cart.map(key));
      
      for (let i = 0; i < itemIndex; i++) {
        if (!cartKeys.has(key(pendingRows[i]))) {
          Swal.fire({
            icon: "warning",
            title: "Orden de pago",
            text: `Debe agregar primero: ${pendingRows[i].abrRub}`,
            confirmButtonColor: "#0d1b3e",
          });
          return;
        }
      }

      dispatch({ type: "ADD_CART", payload: itemCart });
      Swal.fire({
        icon: "success",
        title: "Agregado",
        text: `${itemCart.abrRub} agregado al carrito.`,
        confirmButtonColor: "#0d1b3e",
        timer: 1500,
        showConfirmButton: false,
      });
    },
    [dispatch, state.cart]
  );

  const buildTable = useCallback(
    (data) => {
      if (!tableRef.current) return;
      if (tabulatorInstance.current) {
        tabulatorInstance.current.destroy();
      }

      // Helper: extract number from rubro name (e.g., "1" from "PENSION 1/10")
      const getRubroNum = (abrRub) => {
        const match = abrRub.match(/(\d+)\/\d+/);
        return match ? parseInt(match[1], 10) : 999;
      };

      // Sort data by rubro number for display
      const sortedData = [...data].sort((a, b) => getRubroNum(a.abrRub) - getRubroNum(b.abrRub));

      // Helper: check if a row is already in the cart
      const cartKey = (i) => i.periodo + i.codNiv + i.codEst + i.codRub;
      const cartKeys = new Set(state.cart.map(cartKey));
      const isInCart = (row) => cartKeys.has(cartKey(row));

      // Sequential order: only enable a row if ALL previous rows are in the cart
      const pendingRows = sortedData.filter((r) => parseFloat(r.valor) > 0);
      const canAddToCart = (row) => {
        if (parseFloat(row.valor) <= 0) return false;
        
        const rowIndex = pendingRows.findIndex((r) => cartKey(r) === cartKey(row));
        if (rowIndex === -1) return false;
        // First pending row is always enabled
        if (rowIndex === 0) return true;
        // All previous rows must be in cart
        for (let i = 0; i < rowIndex; i++) {
          if (!cartKeys.has(cartKey(pendingRows[i]))) {
            return false;
          }
        }
        return true;
      };

      tabulatorInstance.current = new Tabulator(tableRef.current, {
        data: sortedData,
        layout: "fitColumns",
        responsiveLayout: "collapse",
        pagination: "local",
        paginationSize: 15,
        placeholder: "No hay rubros pendientes",
        columns: [
          {
            title: "Periodo",
            field: "periodo",
            widthGrow: 1.2,
          },
          {
            title: "Cod. Est",
            field: "codEst",
            hozAlign: "center",
            headerHozAlign: "center",
            widthGrow: 1,
          },
          {
            title: "Nombres",
            field: "nomEst",
            widthGrow: 2,
          },
          {
            title: "Nivel",
            field: "desCos",
            hozAlign: "center",
            headerHozAlign: "center",
            widthGrow: 1,
          },
          {
            title: "Curso",
            field: "abrCur",
            hozAlign: "center",
            headerHozAlign: "center",
            widthGrow: 1,
          },
          {
            title: "Rubro",
            field: "abrRub",
            widthGrow: 1.5,
            formatter: function (cell) {
              var val = cell.getValue();
              if (val && val.includes("TRANSP")) {
                return (
                  '<span style="display:inline-flex;align-items:center;gap:4px;">🚌 ' +
                  val +
                  "</span>"
                );
              }
              return val;
            },
          },
          {
            title: "Valor",
            field: "valor",
            hozAlign: "right",
            headerHozAlign: "right",
            widthGrow: 1,
            formatter: function (cell) {
              return "$ " + ccyFormat(cell.getValue());
            },
          },
          {
            title: "Estado",
            hozAlign: "center",
            headerHozAlign: "center",
            widthGrow: 1,
            formatter: function (cell) {
              var val = cell.getRow().getData().valor;
              if (val > 0) {
                return '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:0.78rem;font-weight:700;background:#fff3e0;color:#e65100;border:1px solid #ffcc80;">⏳ Pendiente</span>';
              }
              return '<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:0.78rem;font-weight:700;background:#e8f5e9;color:#2e7d32;border:1px solid #a5d6a7;">✅ Pagado</span>';
            },
          },
          {
            title: "",
            field: "codRub",
            hozAlign: "center",
            headerSort: false,
            width: 140,
            formatter: function (cell) {
              var rowData = cell.getRow().getData();
              var inCart = isInCart(rowData);
              var canAdd = canAddToCart(rowData);
              
              if (inCart) {
                return '<button class="btn-add-cart disabled" disabled style="padding:6px 14px;border-radius:6px;border:none;background:#e0e3e8;color:#999;cursor:not-allowed;font-size:0.8rem;font-weight:600;">✅ En carrito</button>';
              }
              if (!canAdd) {
                return '<button class="btn-add-cart disabled" disabled style="padding:6px 14px;border-radius:6px;border:none;background:#ffebee;color:#c62828;cursor:not-allowed;font-size:0.8rem;font-weight:600;" title="Debe agregar primero los rubros anteriores">🔒 Bloqueado</button>';
              }
              return '<button class="btn-add-cart" style="padding:6px 14px;border-radius:6px;border:none;background:linear-gradient(135deg,#1565c0,#2196f3);color:#fff;cursor:pointer;font-size:0.8rem;font-weight:600;transition:all 0.2s;">🛒 Agregar</button>';
            },
            cellClick: function (e, cell) {
              var rowData = cell.getRow().getData();
              if (!isInCart(rowData) && canAddToCart(rowData)) {
                addCart(rowData, sortedData);
              }
            },
          },
        ],
      });
    },
    [state.cart, addCart]
  );

  const filtroDocumentos = (tipo) => {
    let rowsTemp = [];
    if (tipo === "T") {
      rowsTemp = rowsAll.filter((r) => r.abrRub.includes("TRANSP"));
      if (rowsTemp.length > 0) {
        setActiveFilter("T");
        buildTable(rowsTemp);
        totales(rowsTemp);
      }
    } else if (tipo === "P") {
      rowsTemp = rowsAll.filter((r) => r.abrRub.includes("PENSION"));
      if (rowsTemp.length > 0) {
        setActiveFilter("P");
        buildTable(rowsTemp);
        totales(rowsTemp);
      }
    } else {
      setActiveFilter("all");
      buildTable(rowsAll);
      totales(rowsAll);
    }
  };

  useEffect(() => {
    setLoading(true);
    httpGet(urlDashBoard + props.codEst, state.token)
      .then((res) => {
        if (res.cod === 0) {
          if (res.data.error) {
            setError(true);
            return;
          }
          setRowsAll(res.data);
          totales(res.data);
          // Defer table build to next tick after render
          setTimeout(() => buildTable(res.data), 0);
        } else {
          setRowsAll([]);
        }
      })
      .catch(() => setRowsAll([]))
      .finally(() => setLoading(false));
  }, [props.codEst, state.token]);

  // Rebuild table when cart changes to update button states
  useEffect(() => {
    if (rowsAll.length > 0 && !loading && tabulatorInstance.current) {
      // Apply current filter when rebuilding
      let dataToRebuild = rowsAll;
      if (activeFilter === "T") {
        dataToRebuild = rowsAll.filter((r) => r.abrRub.includes("TRANSP"));
      } else if (activeFilter === "P") {
        dataToRebuild = rowsAll.filter((r) => r.abrRub.includes("PENSION"));
      }
      if (dataToRebuild.length > 0) {
        // Use setTimeout to break the dependency cycle
        setTimeout(() => buildTable(dataToRebuild), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cart]);

  useEffect(() => {
    return () => {
      if (tabulatorInstance.current) {
        tabulatorInstance.current.destroy();
      }
    };
  }, []);

  const totales = (data) => {
    let sumatoria = 0;
    data.forEach((item) => {
      sumatoria = sumatoria + parseFloat(item.valor);
    });
    setTotal(sumatoria.toFixed(2));
  };

  if (loading) {
    return (
      <div className="modern-spinner fade-in">
        <div className="spinner-ring"></div>
        <span>Cargando rubros del estudiante...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-card slide-in-up">
        <div className="modern-alert modern-alert-error">
          <span style={{ fontSize: "1.3rem" }}>⚠️</span>
          <div>
            <strong>Error al cargar datos</strong>
            <br />
            Hubo un error al traer los datos —{" "}
            <a href="/pension/rubros" className="modern-link">
              Reintentar
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="slide-in-up">
      {/* Back Navigation */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        <button 
          onClick={() => navigate("/pension/list")} 
          className="btn-modern btn-outline"
          style={{ padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 8, width: 'auto' }}
        >
          ⬅️ Volver a Estudiantes
        </button>
        <button 
          onClick={() => navigate("/menuMain")} 
          className="btn-modern btn-outline"
          style={{ padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 8, width: 'auto' }}
        >
          🏠 Menú Principal
        </button>
      </div>

      <div className="filter-btn-group">
        <button
          className={"filter-btn" + (activeFilter === "all" ? " active" : "")}
          onClick={() => filtroDocumentos("A")}
        >
          📄 Todos
        </button>
        <button
          className={"filter-btn" + (activeFilter === "T" ? " active" : "")}
          onClick={() => filtroDocumentos("T")}
        >
          🚌 Solo Transporte
        </button>
        <button
          className={"filter-btn" + (activeFilter === "P" ? " active" : "")}
          onClick={() => filtroDocumentos("P")}
        >
          📚 Solo Pensiones
        </button>
      </div>

      <div className="table-modern-wrapper">
        <div className="table-modern-toolbar">
          <h3>📋 Rubros Pendientes</h3>
          <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>
            Total: $ {total}
          </div>
        </div>
        <div ref={tableRef}></div>
      </div>
    </div>
  );
}
